import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("loading");
  const [isTrial, setIsTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    activateSubscription();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activateSubscription = async () => {
    try {
      const { data } = await api.get(`/subscriptions/activate/${sessionId}`);
      if (data.activated) {
        setStatus("success");
        setIsTrial(data.is_trial || false);
        if (data.subscription?.trial_ends_at) {
          setTrialEnd(new Date(data.subscription.trial_ends_at).toLocaleDateString("fr-FR"));
        }
        // Rafraîchir les données utilisateur pour mettre à jour has_active_subscription
        await refreshUser();
      } else {
        setStatus("pending");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto" />
          <p className="text-slate-600">Activation de votre abonnement...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4 max-w-md">
          {isTrial ? (
            <Gift className="w-16 h-16 text-purple-500 mx-auto" />
          ) : (
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
          )}
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {isTrial ? "Essai gratuit activé !" : "Abonnement activé !"}
          </h1>
          <p className="text-slate-500">
            {isTrial ? (
              <>
                Votre essai gratuit de 14 jours est actif jusqu'au <strong>{trialEnd}</strong>.
                <br />
                Vous avez accès à toutes les fonctionnalités. La facturation démarrera automatiquement à la fin de l'essai.
              </>
            ) : (
              "Votre abonnement coach est maintenant actif. Vous avez accès à toutes les fonctionnalités."
            )}
          </p>
          <Button onClick={() => navigate("/coach/dashboard")} className="rounded-full">
            Aller au dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center space-y-4 max-w-md">
        <XCircle className="w-16 h-16 text-red-400 mx-auto" />
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {status === "pending" ? "Paiement en attente" : "Erreur"}
        </h1>
        <p className="text-slate-500">
          {status === "pending"
            ? "Votre paiement est en cours de traitement. Réessayez dans quelques instants."
            : "Une erreur est survenue lors de l'activation de votre abonnement."}
        </p>
        <Button onClick={() => navigate("/coach/subscription")} variant="outline" className="rounded-full">
          Retour aux abonnements
        </Button>
      </div>
    </div>
  );
}
